import { formatLocalDate } from '../utils/localDateTime';
import { confirmApp, showAppAlert } from '../common/appDialog';

import React, { useEffect, useState } from 'react';
import { db } from '../../firebaseConfig'; 
import { 
    collection, 
    addDoc, 
    getDocs, 
    serverTimestamp, 
    query, 
    where,
    orderBy,   // Add this
    deleteDoc, // Add this
    doc        // Add this
} from 'firebase/firestore'; 
import { ICONS } from './constants';
import type { AnalysisOrPlanResult } from '@types';
import Loader from './Loader';

// Interface reflecting the structure saved in ThreatGroupBriefing.tsx
interface SavedReportDoc {
    id: string;
    savedAt: string;
    type?: string;
    [key: string]: any; // Allow for flexible report properties
}

interface SavedReportsListProps {
    onSelectReport: (report: AnalysisOrPlanResult) => void;
    onBack: () => void;
    organizationId: string;
    readOnly?: boolean;
}

const SavedReportsList: React.FC<SavedReportsListProps> = ({ onSelectReport, onBack, organizationId, readOnly = false }) => {
    const [reports, setReports] = useState<SavedReportDoc[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchReports = async () => {
            if (!db) {
                console.warn("Database not initialized");
                setIsLoading(false);
                return;
            }
            try {
                const q = query(
                    collection(db, 'saved_intel'),
                    where('organizationId', '==', organizationId),
                    orderBy('savedAt', 'desc')
                );
                const querySnapshot = await getDocs(q);
                const fetched: SavedReportDoc[] = querySnapshot.docs.map((d: any) => ({
                    id: d.id,
                    ...d.data()
                } as SavedReportDoc)).filter(report => report.isMapping !== true);
                setReports(fetched);
            } catch (error) {
                console.error("Error fetching reports:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchReports();
    }, [organizationId]);

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!await confirmApp("Are you sure you want to permanently delete this report?")) return;
        
        if (!db) {
            void showAppAlert("Database connection error");
            return;
        }

        try {
            await deleteDoc(doc(db, 'saved_intel', id));
            setReports(prev => prev.filter(r => r.id !== id));
        } catch (error) {
            console.error("Error deleting report:", error);
            void showAppAlert("Failed to delete report.");
        }
    };

    // Helper to safely extract a display title from various report types
    const getReportTitle = (report: SavedReportDoc) => {
        if (report.title) return report.title;
        if (report.groupName) return `Briefing: ${report.groupName}`;
        if (report.country) return `Report: ${report.country}`;
        if (report.reportTitle) return report.reportTitle; // For composite reports
        return 'Untitled Intelligence Report';
    };

    return (
        <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-200">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">Saved Reports Library</h2>
                    <p className="text-sm text-slate-500">Access and manage your saved intelligence products.</p>
                </div>
                <button 
                    onClick={onBack} 
                    className="text-sm font-medium text-slate-600 hover:text-blue-600 bg-slate-100 hover:bg-blue-50 px-4 py-2 rounded-md transition-colors"
                >
                    Close Library
                </button>
            </div>

            {isLoading ? <Loader message="Retrieving archived reports..." /> : (
                <div className="grid grid-cols-1 gap-4">
                    {reports.length === 0 ? (
                        <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                            <div className="mx-auto h-12 w-12 text-slate-400 mb-3">
                                {React.cloneElement(ICONS.folder as any, { className: 'h-12 w-12' })}
                            </div>
                            <p className="text-slate-500 font-medium">No saved reports found.</p>
                            <p className="text-sm text-slate-400">Generate and save analysis from the dashboards to see them here.</p>
                        </div>
                    ) : (
                        reports.map(report => (
                            <div 
                                key={report.id} 
                                onClick={() => onSelectReport(report as unknown as AnalysisOrPlanResult)}
                                className="group bg-white p-5 rounded-lg border border-slate-200 hover:shadow-md hover:border-blue-400 cursor-pointer transition-all flex justify-between items-center"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-100 transition-colors mt-1">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-slate-800 text-lg group-hover:text-blue-700 transition-colors">{getReportTitle(report)}</h3>
                                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                            <span className="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                {formatLocalDate(report.savedAt)}
                                            </span>
                                            <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                            <span className="uppercase tracking-wider font-semibold text-slate-400">{report.type || 'Report'}</span>
                                        </div>
                                    </div>
                                </div>
                                {!readOnly && <button
                                    onClick={(e) => handleDelete(report.id, e)}
                                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                                    title="Delete Report"
                                >
                                    {React.cloneElement(ICONS.trash as any, { className: 'h-5 w-5' })}
                                </button>}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default SavedReportsList;
